// src/pages/admin/analytics/AnalyticsDashboard.jsx
import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Package,
  ShieldAlert,
  Trash2,
  Calendar,
  Filter,
  RefreshCw,
  Download,
  Plus,
  Zap,
  Building,
  Calculator,
  FileText,
  ShoppingCart,
} from 'lucide-react';
import toast from 'react-hot-toast';

import BranchService from '../../../services/branchService';
import analyticsService from '../../../services/analyticsService';

import FinancialOverviewTab from './components/FinancialOverviewTab';
import ProductProfitabilityTab from './components/ProductProfitabilityTab';
import RecipeVarianceTheftTab from './components/RecipeVarianceTheftTab';
import ManualWastageLogTab from './components/ManualWastageLogTab';
import MaterialConsumptionForecastTab from './components/MaterialConsumptionForecastTab';
import RecipeCalculatorTab from './components/RecipeCalculatorTab';
import ProductFoodCostReportTab from './components/ProductFoodCostReportTab';
import RawMaterialConsumptionReportTab from './components/RawMaterialConsumptionReportTab';
import AddWastageModal from './components/AddWastageModal';

export const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tab: routeTab } = useParams();

  // Tab State
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'product-profitability' | 'recipe-variance' | 'wastage' | 'forecast' | 'calculator'

  // Filter States
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const getLocalDateString = (d = new Date()) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [timeRange, setTimeRange] = useState('today'); // 'today' | 'week' | 'month' | 'custom' | 'all'
  const [startDate, setStartDate] = useState(getLocalDateString());
  const [endDate, setEndDate] = useState(getLocalDateString());

  // Data States
  const [overviewData, setOverviewData] = useState(null);
  const [profitabilityData, setProfitabilityData] = useState(null);
  const [recipeVarianceData, setRecipeVarianceData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [wastageLogs, setWastageLogs] = useState([]);
  const [wastageSummary, setWastageSummary] = useState({});

  const [loading, setLoading] = useState(false);
  const [isWastageModalOpen, setIsWastageModalOpen] = useState(false);

  // Sync route param tab with activeTab
  useEffect(() => {
    if (location.pathname.includes('/product-profitability')) {
      setActiveTab('product-profitability');
    } else if (location.pathname.includes('/food-cost-report')) {
      setActiveTab('food-cost-report');
    } else if (location.pathname.includes('/consumption-report')) {
      setActiveTab('consumption-report');
    } else if (location.pathname.includes('/recipe-variance')) {
      setActiveTab('recipe-variance');
    } else if (location.pathname.includes('/wastage')) {
      setActiveTab('wastage');
    } else if (location.pathname.includes('/forecast')) {
      setActiveTab('forecast');
    } else if (location.pathname.includes('/calculator')) {
      setActiveTab('calculator');
    } else {
      setActiveTab('overview');
    }
  }, [location.pathname]);

  // Load Branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await BranchService.getAllBranches();
        const rawData = res?.data?.branches || res?.branches || res?.data?.data || res?.data || [];
        setBranches(Array.isArray(rawData) ? rawData : []);
      } catch (err) {
        console.error('Failed to load branches:', err);
      }
    };
    fetchBranches();
  }, []);

  // Compute date range filters
  const getFilterParams = () => {
    const params = {};
    if (selectedBranch) params.branch = selectedBranch;

    const now = new Date();

    if (timeRange === 'today') {
      const todayStr = getLocalDateString(now);
      params.startDate = todayStr;
      params.endDate = todayStr;
    } else if (timeRange === 'week') {
      const pastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      params.startDate = getLocalDateString(pastWeek);
      params.endDate = getLocalDateString(now);
    } else if (timeRange === 'month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      params.startDate = getLocalDateString(firstDay);
      params.endDate = getLocalDateString(now);
    } else if (timeRange === 'custom') {
      const todayString = getLocalDateString(now);
      params.startDate = startDate || todayString;
      params.endDate = endDate || todayString;
    }
    // 'all' passes no startDate/endDate to query all historical data

    return params;
  };

  // Fetch Tab Data
  const loadData = async () => {
    try {
      setLoading(true);
      const params = getFilterParams();

      if (activeTab === 'overview') {
        const data = await analyticsService.getProfitDashboard(params);
        setOverviewData(data?.data || data);
      } else if (activeTab === 'product-profitability' || activeTab === 'food-cost-report') {
        const data = await analyticsService.getProductProfitability(params);
        setProfitabilityData(data?.data || data);
      } else if (activeTab === 'recipe-variance' || activeTab === 'consumption-report') {
        const data = await analyticsService.getRecipeVariance(params);
        setRecipeVarianceData(data?.data || data);
      } else if (activeTab === 'wastage') {
        const [logsRes, summaryRes] = await Promise.all([
          analyticsService.getWastageLogs(params),
          analyticsService.getWastageSummary(params),
        ]);
        setWastageLogs(logsRes?.data?.logs || logsRes?.logs || []);
        setWastageSummary(summaryRes?.data || summaryRes || {});
      } else if (activeTab === 'forecast') {
        const data = await analyticsService.getForecastMetrics(params);
        setForecastData(data?.data || data);
      }
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      toast.error('Failed to load analytics metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab, selectedBranch, timeRange, startDate, endDate]);

  const handleTabChange = (tabKey, path) => {
    setActiveTab(tabKey);
    navigate(path);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
        <div>
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-tr from-orange-500 to-amber-500 rounded-xl text-white shadow-md">
              <BarChart3 className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                Analytics & Theft Control Suite
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                Real-time GRN cost analysis, net profit margins, recipe shrinkage, and manual wastage tracking.
              </p>
            </div>
          </div>
        </div>

        {/* Global Controls & Filters */}
        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
          {/* Branch Filter */}
          <div className="relative">
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl bg-white text-gray-800 text-xs font-semibold shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 hover:border-gray-400 transition-all cursor-pointer pr-8"
            >
              <option value="">🏢 All Branches</option>
              {branches.map((b) => (
                <option key={b._id || b.id} value={b._id || b.id}>
                  📍 {b.name}
                </option>
              ))}
            </select>
          </div>

          {/* Time Range Selector */}
          <div className="flex items-center bg-gray-100 p-1 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700">
            <button
              onClick={() => setTimeRange('all')}
              className={`px-3 py-1.5 rounded-lg transition-all ${
                timeRange === 'all' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
              }`}
            >
              All Time
            </button>
            <button
              onClick={() => setTimeRange('today')}
              className={`px-3 py-1.5 rounded-lg transition-all ${
                timeRange === 'today' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setTimeRange('week')}
              className={`px-3 py-1.5 rounded-lg transition-all ${
                timeRange === 'week' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => setTimeRange('month')}
              className={`px-3 py-1.5 rounded-lg transition-all ${
                timeRange === 'month' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
              }`}
            >
              This Month
            </button>
            <button
              onClick={() => setTimeRange('custom')}
              className={`px-3 py-1.5 rounded-lg transition-all ${
                timeRange === 'custom' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
              }`}
            >
              Custom
            </button>
          </div>

          {/* Custom Date Inputs */}
          {timeRange === 'custom' && (
            <div className="flex items-center space-x-2 text-xs">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-2.5 py-1.5 border border-gray-300 rounded-lg"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-2.5 py-1.5 border border-gray-300 rounded-lg"
              />
            </div>
          )}

          {/* Refresh Button */}
          <button
            onClick={loadData}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
            title="Refresh Data"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin text-orange-500' : ''}`} />
          </button>
        </div>
      </div>

      {/* Main Sub-tabs Navigation Bar */}
      <div className="bg-white rounded-2xl p-2 border border-gray-200 shadow-sm flex flex-wrap gap-2">
        <button
          onClick={() => handleTabChange('overview', '/admin/analytics')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-bold text-xs tracking-wide transition-all ${
            activeTab === 'overview'
              ? 'bg-gray-900 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <DollarSign className="h-4 w-4" />
          <span>Financial P&L Overview</span>
        </button>

        <button
          onClick={() => handleTabChange('product-profitability', '/admin/analytics/product-profitability')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-bold text-xs tracking-wide transition-all ${
            activeTab === 'product-profitability'
              ? 'bg-gray-900 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Package className="h-4 w-4" />
          <span>Product Profitability</span>
        </button>

        <button
          onClick={() => handleTabChange('food-cost-report', '/admin/analytics/food-cost-report')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-bold text-xs tracking-wide transition-all ${
            activeTab === 'food-cost-report'
              ? 'bg-emerald-700 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <FileText className="h-4 w-4" />
          <span>Food Cost & Profitability Report</span>
        </button>

        <button
          onClick={() => handleTabChange('recipe-variance', '/admin/analytics/recipe-variance')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-bold text-xs tracking-wide transition-all ${
            activeTab === 'recipe-variance'
              ? 'bg-amber-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <ShieldAlert className="h-4 w-4" />
          <span>Recipe & Theft Variance</span>
        </button>

        <button
          onClick={() => handleTabChange('consumption-report', '/admin/analytics/consumption-report')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-bold text-xs tracking-wide transition-all ${
            activeTab === 'consumption-report'
              ? 'bg-orange-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <ShoppingCart className="h-4 w-4" />
          <span>Raw Material Consumption Report</span>
        </button>

        <button
          onClick={() => handleTabChange('wastage', '/admin/analytics/wastage')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-bold text-xs tracking-wide transition-all ${
            activeTab === 'wastage'
              ? 'bg-rose-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Trash2 className="h-4 w-4" />
          <span>Manual Wastage Entry</span>
        </button>

        <button
          onClick={() => handleTabChange('forecast', '/admin/analytics/forecast')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-bold text-xs tracking-wide transition-all ${
            activeTab === 'forecast'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Zap className="h-4 w-4" />
          <span>Demand Forecast</span>
        </button>

        <button
          onClick={() => handleTabChange('calculator', '/admin/analytics/calculator')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-bold text-xs tracking-wide transition-all ${
            activeTab === 'calculator'
              ? 'bg-orange-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Calculator className="h-4 w-4" />
          <span>Recipe & Product Calculator</span>
        </button>
      </div>

      {/* Tab Content Display */}
      <div>
        {activeTab === 'overview' && (
          <FinancialOverviewTab data={overviewData} loading={loading} onRefresh={loadData} />
        )}

        {activeTab === 'product-profitability' && (
          <ProductProfitabilityTab data={profitabilityData} loading={loading} />
        )}

        {activeTab === 'food-cost-report' && (
          <ProductFoodCostReportTab
            data={profitabilityData}
            loading={loading}
            branches={branches}
            selectedBranch={selectedBranch}
            setSelectedBranch={setSelectedBranch}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            onRefresh={loadData}
          />
        )}

        {activeTab === 'recipe-variance' && (
          <RecipeVarianceTheftTab data={recipeVarianceData} loading={loading} />
        )}

        {activeTab === 'consumption-report' && (
          <RawMaterialConsumptionReportTab data={recipeVarianceData} loading={loading} onRefresh={loadData} />
        )}

        {activeTab === 'wastage' && (
          <ManualWastageLogTab
            logs={wastageLogs}
            summary={wastageSummary}
            loading={loading}
            onOpenAddModal={() => setIsWastageModalOpen(true)}
            onRefresh={loadData}
          />
        )}

        {activeTab === 'forecast' && (
          <MaterialConsumptionForecastTab data={forecastData} loading={loading} />
        )}

        {activeTab === 'calculator' && (
          <RecipeCalculatorTab
            selectedBranch={selectedBranch}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </div>

      {/* Add Wastage Modal */}
      <AddWastageModal
        isOpen={isWastageModalOpen}
        onClose={() => setIsWastageModalOpen(false)}
        onSuccess={loadData}
        branches={branches}
        currentBranch={selectedBranch}
      />
    </div>
  );
};

export default AnalyticsDashboard;
